/**
 * Frontend re-aggregation for the Company Score panel.
 *
 * The backend (stock_analyzer/services/company_score.py) does the NORMALIZATION
 * — turning each raw metric into a 0-100 sub-score — and ships per-signal
 * sub-scores + default weights for every ticker. This module only re-runs the
 * cheap WEIGHTED-MEAN AGGREGATION so the sliders re-score every ticker instantly
 * with no API round-trip.
 *
 * `aggregate()` is a line-for-line port of the Python `aggregate()` (and of
 * macroScore.js). The Company Score is a single flat family of signals, so
 * weights are a flat {key: weight} map (not nested per family like macro).
 */

const LABELS = ['Avoid', 'Weak', 'Fair', 'Good', 'Strong'];

/** Band color for a 0-100 score (shared by the gauge, table cells, and modal). */
export function scoreColor(value) {
  if (value == null) return '#6b7280';
  if (value < 20) return '#dc2626';
  if (value < 40) return '#ea580c';
  if (value < 60) return '#eab308';
  if (value < 80) return '#65a30d';
  return '#16a34a';
}

export function scoreLabel(value) {
  if (value == null) return 'No data';
  if (value < 20) return LABELS[0];
  if (value < 40) return LABELS[1];
  if (value < 60) return LABELS[2];
  if (value < 80) return LABELS[3];
  return LABELS[4];
}

const round = (v, dp) => {
  const f = 10 ** dp;
  return Math.round(v * f) / f;
};

/**
 * Weighted-mean aggregate of one ticker's per-signal sub-scores.
 *
 * @param {Array<{key,label,category,subscore}>} signals  per-signal sub-scores
 * @param {Object<string,number>} weights                 flat key -> weight
 * @returns {{value, label, coverage, degraded, categories, signals}}
 */
export function aggregate(signals, weights) {
  let intendedWeight = 0;
  const surviving = [];
  const enriched = [];

  for (const sig of signals || []) {
    const w = Number(weights?.[sig.key]) || 0;
    intendedWeight += Math.max(w, 0);
    const item = { ...sig, weight: w, contribution: null };
    if (sig.subscore != null && w > 0) surviving.push({ sig, w });
    enriched.push(item);
  }

  const totalWeight = surviving.reduce((s, { w }) => s + w, 0);
  if (!surviving.length || totalWeight === 0) {
    return {
      value: null,
      label: scoreLabel(null),
      coverage: 0,
      degraded: true,
      categories: [],
      signals: enriched,
    };
  }

  const value = surviving.reduce((s, { sig, w }) => s + sig.subscore * w, 0) / totalWeight;

  const contribByKey = {};
  for (const { sig, w } of surviving) {
    contribByKey[sig.key] = (sig.subscore * w) / totalWeight;
  }
  for (const item of enriched) {
    if (item.key in contribByKey) item.contribution = round(contribByKey[item.key], 2);
  }

  // Per-category roll-up (weighted mean within each category).
  const cats = {};
  for (const { sig, w } of surviving) {
    (cats[sig.category] ||= []).push({ subscore: sig.subscore, w });
  }
  const categories = Object.entries(cats).map(([name, members]) => {
    const cw = members.reduce((s, m) => s + m.w, 0);
    const csub = cw ? members.reduce((s, m) => s + m.subscore * m.w, 0) / cw : null;
    return {
      name,
      subscore: csub == null ? null : round(csub, 1),
      weight: round(cw, 4),
      signals: (signals || []).filter((s) => s.category === name).map((s) => s.key),
    };
  });
  categories.sort((a, b) => (b.subscore ?? -1) - (a.subscore ?? -1));

  const coverage = intendedWeight ? totalWeight / intendedWeight : 0;
  return {
    value: round(value, 1),
    label: scoreLabel(value),
    coverage: round(coverage, 3),
    degraded: coverage < 0.999,
    categories,
    signals: enriched,
  };
}

/**
 * The flat default-weights map the sliders mutate. Defensive against a missing
 * block (falls back to {} so the panel still renders, just with no weights).
 */
export function defaultWeightsFrom(scoreBlock) {
  return { ...(scoreBlock?.default_weights || {}) };
}

/** Stable category order for table columns / slider groups. */
export const CATEGORY_ORDER = [
  'Fair Value Upside',
  'Valuation',
  'Profitability',
  'Income',
  'Growth',
  'Estimate Revisions',
  'Technicals',
];

/** Minimum available-weight coverage before a score is flagged low-confidence. */
export const MIN_COVERAGE = 0.6;

/** Short human labels for gate / flag codes (chips + tooltips). */
export const GATE_LABELS = {
  earnings_within_30d: 'Earnings soon',
  principal_risk: 'Balance-sheet risk',
  insufficient_data: 'Thin data',
  illiquid: 'Illiquid',
};

/**
 * Merge the backend's (method-independent) gates with the frontend's
 * method-dependent coverage gate. `result` is an aggregate() output; `backendGates`
 * is the per-ticker `gates` block ({eligible, reasons, flags}) or undefined.
 * Returns { eligible, reasons:[{code,label}], flags:[{code,label}] }.
 */
export function gateState(result, backendGates) {
  const reasons = [...(backendGates?.reasons || [])];
  const flags = [...(backendGates?.flags || [])];
  if (result && result.value != null && result.coverage < MIN_COVERAGE) {
    reasons.push({ code: 'insufficient_data', label: GATE_LABELS.insufficient_data });
  }
  return {
    eligible: reasons.length === 0,
    reasons,
    flags,
  };
}
